import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { Button } from '@material-ui/core'
import clsx from 'clsx'
import React, { FC, useCallback, useMemo, useState } from 'react'
import {
    advancedFilterDefaultColor,
    advancedFilterDefaultHoveredColor,
    listingDialogFilterContainerColor,
} from 'shared/common/productListingDialogColors'
import ProductListingIcon, {
    ProductListingIconStatusType,
    ProductListingIconType,
} from '../Card/ProductListingIcon'
import { productOptions } from '../ProductListingDialog.types'
import { useStyles } from './AdvancedFilters.styles'

type AdvancedProductFilterProps = {
    product: Record<string, boolean>
    handleClick: (event: Record<string, boolean>) => void
}

const AdvancedProductFilter: FC<AdvancedProductFilterProps> = ({
    product,
    handleClick,
}) => {
    const classes = useStyles()
    const [iconHover, setIconHover] = useState(undefined)

    const handleSetIconHover = useCallback(
        (iconType) => () => {
            setIconHover(iconType)
        },
        []
    )

    const productKeys = Object.keys(product)
    const productSelectOptions = useMemo(() => productKeys, [productKeys])

    const handleProductIconClick = useCallback(
        (iconType: string) => () => {
            handleClick({ ...product, [iconType]: !product[iconType] })
        },
        [handleClick, product]
    )

    const productStatus = useCallback(
        (productOption: string) => {
            return !productSelectOptions?.includes(productOption)
                ? ProductListingIconStatusType.DISABLED
                : !!product[productOption]
                    ? ProductListingIconStatusType.ACTIVE
                    : iconHover === productOption
                        ? ProductListingIconStatusType.HOVERED
                        : ProductListingIconStatusType.INITIAL
        },
        [iconHover, productSelectOptions, product]
    )

    return (
        <GridContainer className={classes.filterContainer}>
            <HoverTooltip text={productOptions.ADDED_TO_PROJECT}>
                <Button
                    className={clsx(classes.actionButton, {
                        [classes.iconIsActive]:
                            productStatus(productOptions.ADDED_TO_PROJECT) ===
                            ProductListingIconStatusType.ACTIVE,
                    })}
                    onClick={handleProductIconClick(
                        productOptions.ADDED_TO_PROJECT
                    )}
                    tabIndex={0}
                    data-testid="advancedProductAddedToProject"
                    onMouseOver={handleSetIconHover(ProductListingIconType.ADD)}
                    onMouseLeave={handleSetIconHover(undefined)}
                >
                    <ProductListingIcon
                        iconType={ProductListingIconType.ADD}
                        iconColors={{
                            default: advancedFilterDefaultColor,
                            initial: advancedFilterDefaultColor,
                            hovered: advancedFilterDefaultHoveredColor,
                            active: listingDialogFilterContainerColor,
                        }}
                        iconSize={24}
                        iconStatusType={productStatus(
                            productOptions.ADDED_TO_PROJECT
                        )}
                        style={{
                            cursor:
                                iconHover === ProductListingIconType.ADD
                                    ? 'pointer'
                                    : 'default',
                        }}
                    />
                </Button>
            </HoverTooltip>
            <HoverTooltip text={productOptions.FLAGGED_FOR_REVIEW}>
                <Button
                    className={clsx(classes.actionButton, {
                        [classes.iconIsActive]:
                            productStatus(productOptions.FLAGGED_FOR_REVIEW) ===
                            ProductListingIconStatusType.ACTIVE,
                    })}
                    onClick={handleProductIconClick(
                        productOptions.FLAGGED_FOR_REVIEW
                    )}
                    data-testid="advancedProductFlaggedForReview"
                    onMouseOver={handleSetIconHover(
                        ProductListingIconType.FLAGFORREVIEW
                    )}
                    onMouseLeave={handleSetIconHover(undefined)}
                    tabIndex={0}
                >
                    <ProductListingIcon
                        iconType={ProductListingIconType.FLAGFORREVIEW}
                        iconColors={{
                            default: advancedFilterDefaultColor,
                            initial: advancedFilterDefaultColor,
                            hovered: advancedFilterDefaultHoveredColor,
                            active: listingDialogFilterContainerColor,
                        }}
                        iconSize={24}
                        iconStatusType={productStatus(
                            productOptions.FLAGGED_FOR_REVIEW
                        )}
                        style={{
                            cursor:
                                iconHover ===
                                    ProductListingIconType.FLAGFORREVIEW
                                    ? 'pointer'
                                    : 'default',
                        }}
                    />
                </Button>
            </HoverTooltip>
        </GridContainer>
    )
}

export default AdvancedProductFilter
